import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import type { MarkingEditorScale } from '../utils'
import type { ExtendedMarkingFormData } from '../types'
import { isValidScale } from '../utils/persistence/persistence'

/**
 * Параметры для хука управления масштабом
 */
export interface UseMarkingScaleParams {
    /**
     * Сохраненные данные из БД
     */
    savedData?: Partial<ExtendedMarkingFormData> | null
    /**
     * Данные из localStorage (для миграции)
     */
    localData?: { scale?: MarkingEditorScale | null } | null
    /**
     * Открыто ли модальное окно (для загрузки масштаба при открытии)
     */
    isOpen?: boolean
}

/**
 * Хук для управления масштабом canvas редактора маркировки
 * Отвечает за загрузку, валидацию и обновление масштаба
 */
export function useMarkingScale(params: UseMarkingScaleParams = {}) {
    const { savedData, localData, isOpen = false } = params

    // Состояние для масштаба
    const [scale, setScale] = useState<MarkingEditorScale | null>(null)

    // Ref для хранения актуального масштаба - используется при сохранении
    // Это гарантирует, что scale будет доступен даже если state еще не обновился
    const scaleRef = useRef<MarkingEditorScale | null>(null)

    // Ref для отслеживания, был ли масштаб уже загружен, чтобы избежать циклов
    const scaleLoadedRef = useRef(false)

    // Получаем scale из savedData (БД) - новые поля добавлены в SYN-829
    const savedScale = useMemo(() => {
        if (!savedData) return null

        const extendedData = savedData as typeof savedData & {
            mark_media_canvas_width?: number | null
            mark_media_canvas_height?: number | null
            mark_media_original_width?: number | null
            mark_media_original_height?: number | null
        }

        if (
            extendedData.mark_media_canvas_width &&
            extendedData.mark_media_canvas_height &&
            extendedData.mark_media_original_width &&
            extendedData.mark_media_original_height
        ) {
            const scaleData: MarkingEditorScale = {
                canvasWidth: extendedData.mark_media_canvas_width,
                canvasHeight: extendedData.mark_media_canvas_height,
                originalWidth: extendedData.mark_media_original_width,
                originalHeight: extendedData.mark_media_original_height,
            }

            if (isValidScale(scaleData)) {
                return scaleData
            }
        }

        return null
    }, [savedData])

    // Загружаем масштаб из БД или localStorage при открытии
    useEffect(() => {
        if (!isOpen) {
            // Сбрасываем флаг загрузки при закрытии
            scaleLoadedRef.current = false
            setScale(null)
            scaleRef.current = null
            return
        }

        const scaleToLoad = savedScale ?? localData?.scale

        if (!scaleLoadedRef.current && scaleToLoad) {
            try {
                if (isValidScale(scaleToLoad)) {
                    scaleRef.current = scaleToLoad
                    setScale(scaleToLoad)
                    scaleLoadedRef.current = true
                } else {
                    setScale(null)
                    scaleRef.current = null
                }
            } catch (error) {
                setScale(null)
                scaleRef.current = null
            }
        }
    }, [isOpen, savedScale, localData?.scale])

    /**
     * Обработчик изменения масштаба с валидацией
     */
    const handleScaleChange = useCallback(
        (newScale: MarkingEditorScale) => {
            // Валидация входных данных
            if (!isValidScale(newScale)) {
                return
            }

            // Сохраняем новый масштаб в ref (всегда актуальный для сохранения)
            scaleRef.current = newScale

            // Обновляем состояние для UI
            setScale((prevScale) => {
                if (
                    !prevScale ||
                    prevScale.canvasWidth !== newScale.canvasWidth ||
                    prevScale.canvasHeight !== newScale.canvasHeight ||
                    prevScale.originalWidth !== newScale.originalWidth ||
                    prevScale.originalHeight !== newScale.originalHeight
                ) {
                    return newScale
                }
                return prevScale
            })
        },
        []
    )

    /**
     * Получить актуальный масштаб (из ref, если доступен, иначе из state)
     */
    const getCurrentScale = useCallback((): MarkingEditorScale | null => {
        return scaleRef.current ?? scale
    }, [scale])

    return {
        scale,
        handleScaleChange,
        getCurrentScale,
    }
}
